import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail, KeyRound, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { forgotPassword, resetPassword } from '../../services/auth';
import { AuthLayoutCentered } from '../../components/auth-layout';
import ghostIcon from '../../assets/ghost.png';

const INPUT_CLASS =
  'h-11 w-full rounded-xl pl-11 pr-4 text-sm outline-none transition-all duration-200 placeholder:text-white/20 focus:ring-2 focus:ring-[#AD34FE]/40';
const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#f5f0fa',
};
const CARD_STYLE = {
  background: 'rgba(26, 14, 36, 0.7)',
  border: '1px solid rgba(173, 52, 254, 0.15)',
  backdropFilter: 'blur(32px)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset',
};
const ICON_WRAP = 'absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none';
const LABEL_CLASS = 'text-xs font-medium uppercase tracking-wider';
const LABEL_STYLE = { color: 'rgba(255,255,255,0.4)' };

function PasswordField({ id, name, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <div className={ICON_WRAP} style={{ color: 'rgba(255,255,255,0.3)' }}>
        <Lock className="h-4 w-4" />
      </div>
      <input
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        required
        autoComplete="new-password"
        className={`${INPUT_CLASS} pr-11`}
        style={INPUT_STYLE}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center transition-colors"
        style={{ color: 'rgba(255,255,255,0.3)', background: 'transparent', border: 'none' }}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function SubmitButton({ loading, label }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="mt-1 h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
      style={{
        color: '#fff',
        background: loading ? 'rgba(173,52,254,0.4)' : 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
        boxShadow: loading ? 'none' : '0 0 32px rgba(173,52,254,0.4), 0 4px 16px rgba(0,0,0,0.3)',
        border: 'none',
      }}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
    </button>
  );
}

const STEP_HEADINGS = [
  { title: 'Esqueceu a senha?', sub: 'Não se preocupe, vamos te ajudar a recuperar seu acesso.' },
  { title: 'Redefinir Senha', sub: 'Insira o código recebido e sua nova senha.' },
  { title: 'Tudo certo!', sub: 'Sua senha foi alterada com sucesso.' },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');

  function sendEmail(e) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.target));
    setIsLoading(true);
    forgotPassword({ email: data.email })
      .then(() => {
        toast.success('Email enviado com sucesso!');
        setEmail(data.email);
        setStep(1);
      })
      .catch(() => {
        toast.error('Não foi possível enviar email');
      })
      .finally(() => setIsLoading(false));
  }

  function changePass(e) {
    e.preventDefault();
    const { token, newPassword, confirmPassword } = Object.fromEntries(new FormData(e.target));
    if (newPassword !== confirmPassword) {
      toast.error('As senhas devem ser iguais');
      return;
    }
    setIsLoading(true);
    resetPassword({ email, token, newPassword })
      .then(() => {
        toast.success('Senha alterada com sucesso!');
        setStep(2);
      })
      .catch(() => {
        toast.error('Não foi possível alterar a senha, verifique se seu token é válido!');
      })
      .finally(() => setIsLoading(false));
  }

  return (
    <AuthLayoutCentered>
      <div className="flex flex-col gap-8">
        {/* Brand */}
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="animate-ghost-float relative">
            <div
              className="animate-ghost-glow absolute inset-0 rounded-full blur-2xl"
              style={{ background: '#AD34FE' }}
            />
            <img
              src={ghostIcon}
              alt="Verboo"
              className="relative h-[72px] w-[72px] object-contain drop-shadow-lg"
            />
          </div>
          <div>
            <h1 className="text-xl font-semibold" style={{ color: '#fff' }}>
              {STEP_HEADINGS[step].title}
            </h1>
            <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {STEP_HEADINGS[step].sub}
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6 flex flex-col gap-5" style={CARD_STYLE}>
          {step === 0 && (
            <form onSubmit={sendEmail} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className={LABEL_CLASS} style={LABEL_STYLE}>
                  Email de Recuperação
                </label>
                <div className="relative">
                  <div className={ICON_WRAP} style={{ color: 'rgba(255,255,255,0.3)' }}>
                    <Mail className="h-4 w-4" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    required
                    autoComplete="email"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
              <SubmitButton loading={isLoading} label="Enviar link de recuperação" />
            </form>
          )}

          {step === 1 && (
            <form onSubmit={changePass} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="token" className={LABEL_CLASS} style={LABEL_STYLE}>
                  Código de Recuperação
                </label>
                <div className="relative">
                  <div className={ICON_WRAP} style={{ color: 'rgba(255,255,255,0.3)' }}>
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <input
                    id="token"
                    name="token"
                    type="text"
                    placeholder="Insira o código"
                    required
                    autoComplete="off"
                    className={INPUT_CLASS}
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="newPassword" className={LABEL_CLASS} style={LABEL_STYLE}>
                  Nova Senha
                </label>
                <PasswordField id="newPassword" name="newPassword" placeholder="••••••••" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="confirmPassword" className={LABEL_CLASS} style={LABEL_STYLE}>
                  Confirmar Senha
                </label>
                <PasswordField id="confirmPassword" name="confirmPassword" placeholder="••••••••" />
              </div>
              <SubmitButton loading={isLoading} label="Redefinir Senha" />
              <button
                type="button"
                onClick={() => setStep(0)}
                className="text-sm text-center transition-colors"
                style={{ color: 'rgba(255,255,255,0.4)', background: 'transparent', border: 'none' }}
              >
                Voltar
              </button>
            </form>
          )}

          {step === 2 && (
            <div className="flex flex-col items-center text-center py-4 gap-4">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ background: 'rgba(173,52,254,0.1)', border: '1px solid rgba(173,52,254,0.2)' }}
              >
                <CheckCircle2 className="h-6 w-6" style={{ color: '#AD34FE' }} />
              </div>
              <div>
                <h3 className="text-base font-medium mb-2" style={{ color: '#fff' }}>
                  Senha alterada!
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Você já pode acessar sua conta com a nova senha cadastrada.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-2 h-11 w-full rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98]"
                style={{
                  color: '#fff',
                  background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)',
                  boxShadow: '0 0 32px rgba(173,52,254,0.4), 0 4px 16px rgba(0,0,0,0.3)',
                  border: 'none',
                }}
              >
                Ir para o Login
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 2 && (
          <div className="flex justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
              style={{
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para o Login
            </Link>
          </div>
        )}
      </div>
    </AuthLayoutCentered>
  );
}
